import { create } from 'zustand';
import type { Profile } from '../types/Profile';
import type { Lab } from '../types/Lab';
import type { ApiServices } from '../hooks/useApi';
import { useUserStore } from './userStore';

// Preference key constants
export const PREFERENCE_KEYS = {
    // Lab settings
    SELECTED_LAB_ID: 'selected_lab_id',
    LAB_PARTNER_CHAT_OPEN: 'lab_partner_chat_open',

    // Feature results display
    FEATURE_RESULTS_SHOW_DESCRIPTION: 'feature_results_show_description',
    FEATURE_RESULTS_SHOW_FORMULA: 'feature_results_show_formula',
    FEATURE_RESULTS_SHOW_TIMESTAMPS: 'feature_results_show_timestamps',
    FEATURE_RESULTS_SORT_FIELD: 'feature_results_sort_field',
    FEATURE_RESULTS_SORT_ORDER: 'feature_results_sort_order',

    // UI preferences
    SHOW_IMPORT_EXPORT: 'show_import_export',
    SHOW_MODEL_TRANSFER: 'show_model_transfer',
    HIDE_ENVIRONMENT_BANNER: 'hide_environment_banner',
    USE_COMPONENT_TEMPLATES: 'use_component_templates',
    SHOW_IDS_IN_HISTORY: 'show_ids_in_history',
    SHOW_TEAM_NAMES: 'show_team_names',
    SIDEBAR_DOMAIN_FOLDERS_EXPANDED: 'sidebar_domain_folders_expanded',
    SIDEBAR_OUTCOME_FOLDERS_EXPANDED: 'sidebar_outcome_folders_expanded',
    SHOW_RESULTS_TRENDS: 'show_results_trends',

    // Tour/onboarding
    DASHBOARD_TOUR_COMPLETED: 'dashboard_tour_completed',
    MODEL_DETAILS_TOUR_COMPLETED: 'model_details_tour_completed',
    ANALYTICS_VIEWED: 'analytics_viewed',
    SMARTPICKS_TAB_FLAG: 'smartpicks_tab_flag',
    TERMS_AND_CONDITIONS_ACCEPTED: 'terms_and_conditions_accepted',
    SHOW_ALL_PICKS: 'show_all_picks',
    PICKS_TIME_FILTER: 'picks_time_filter',
    PICKS_VIEW_MODE_TEAM: 'picks_view_mode_team',
    PICKS_VIEW_MODE_PLAYER: 'picks_view_mode_player',

    // New onboarding preferences
    ONBOARDING_PHASE: 'onboarding_phase',
    WELCOME_TOUR_COMPLETED: 'welcome_tour_completed',
    SMART_PICKS_TOUR_COMPLETED: 'smart_picks_tour_completed',

    // Onboarding checklist milestones
    ONBOARDING_MILESTONE_LAB_CREATED: 'onboarding_milestone_lab_created',
    ONBOARDING_MILESTONE_MODEL_BUILT: 'onboarding_milestone_model_built',
    ONBOARDING_MILESTONE_COMPONENTS_ADDED: 'onboarding_milestone_components_added',
    ONBOARDING_MILESTONE_MODEL_TESTED: 'onboarding_milestone_model_tested',
    ONBOARDING_MILESTONE_RESULTS_REVIEWED: 'onboarding_milestone_results_reviewed',
    ONBOARDING_MILESTONE_MODEL_ITERATED: 'onboarding_milestone_model_iterated',
    ONBOARDING_MILESTONE_MODEL_TRAINED: 'onboarding_milestone_model_trained',
    ONBOARDING_MILESTONE_MODEL_PUBLISHED: 'onboarding_milestone_model_published',
    ONBOARDING_MILESTONE_PICKS_VIEWED: 'onboarding_milestone_picks_viewed',

    // Onboarding UI preferences
    HIDE_ONBOARDING_CHECKLIST: 'hide_onboarding_checklist',
    LAB_PARTNER_PROGRESS_EXPANDED: 'lab_partner_progress_expanded',

    // Feature introductions (one-time contextual tips)
    INTRO_LAB_PARTNER: 'intro_lab_partner',
    INTRO_SMART_PICKS: 'intro_smart_picks',
    INTRO_ANALYTICS: 'intro_analytics',
    INTRO_PUBLISHING: 'intro_publishing',
    INTRO_FEATURES_TAB: 'intro_features_tab',
    INTRO_OPERATIONS: 'intro_operations',
    INTRO_METRICS_POPOVERS: 'intro_metrics_popovers',

    // Video tracking (individual video views)
    // General (Quick Concepts)
    VIDEO_VIEWED_WELCOME_TO_MODDY_AI: 'video_viewed_welcome_to_moddy_ai',
    VIDEO_VIEWED_WHAT_IS_A_LAB: 'video_viewed_what_is_a_lab',
    VIDEO_VIEWED_COMMUNITY_GUIDE: 'video_viewed_community_guide',

    // Model Concepts (Walkthroughs)
    VIDEO_VIEWED_CREATING_A_MODEL: 'video_viewed_creating_a_model',
    VIDEO_VIEWED_ADDING_COMPONENTS: 'video_viewed_adding_components',
    VIDEO_VIEWED_TESTING_YOUR_MODEL: 'video_viewed_testing_your_model',
    VIDEO_VIEWED_REVIEWING_TEST_RESULTS: 'video_viewed_reviewing_test_results',
    VIDEO_VIEWED_ITERATING_ON_MODEL: 'video_viewed_iterating_on_model',
    VIDEO_VIEWED_TRAINING_YOUR_MODEL: 'video_viewed_training_your_model',
    VIDEO_VIEWED_PUBLISHING_YOUR_MODEL: 'video_viewed_publishing_your_model',
    VIDEO_VIEWED_UNDERSTANDING_PICKS: 'video_viewed_understanding_picks',
    VIDEO_VIEWED_WHAT_ARE_SMART_PICKS: 'video_viewed_what_are_smart_picks',

    // Advanced Topics (Deep Dives)
    VIDEO_VIEWED_MODEL_REVIEW: 'video_viewed_model_review',
    VIDEO_VIEWED_PRUNING_MODEL: 'video_viewed_pruning_model',
    VIDEO_VIEWED_EXPANDING_MODEL: 'video_viewed_expanding_model',
    VIDEO_VIEWED_FOCUSING_THEORY: 'video_viewed_focusing_theory',
    VIDEO_VIEWED_BEST_PRACTICES: 'video_viewed_best_practices',
    VIDEO_VIEWED_WHEN_IS_MODEL_DONE: 'video_viewed_when_is_model_done',
    VIDEO_VIEWED_UPDATING_LIVE_MODEL: 'video_viewed_updating_live_model',
    VIDEO_VIEWED_LAB_PARTNER_TIPS: 'video_viewed_lab_partner_tips',

    // Hint dismissals
    HINT_LAB_PARTNER_FIRST_MESSAGE: 'hint_lab_partner_first_message',
    HINT_FEATURES_EMPTY_STATE: 'hint_features_empty_state',
    HINT_SMART_PICKS_FILTERS: 'hint_smart_picks_filters',
    HINT_OPERATION_RESULTS: 'hint_operation_results',

    // Smart Picks preferences
    SMART_PICKS_DOMAIN: 'smart_picks_domain',
    SMART_PICKS_OUTCOME: 'smart_picks_outcome', // Legacy - kept for backwards compatibility
    SMART_PICKS_OUTCOME_PREFIX: 'smart_picks_outcome_', // Per-domain prefix (e.g., smart_picks_outcome_nfl)
    SMART_PICKS_TAB: 'smart_picks_tab',
    SMART_PICKS_STRENGTH_FILTER: 'smart_picks_strength_filter',
    SMART_PICKS_VIEW_MODE: 'smart_picks_view_mode',

    // Display preferences
    ODDS_FORMAT: 'odds_format',

    // Lab Partner preferences
    LAB_PARTNER_LOG_TIMING: 'lab_partner_log_timing',
    LAB_PARTNER_LOG_IDS: 'lab_partner_log_ids',

    // Dashboard widget preferences
    DASHBOARD_CREATOR_SP_DOMAIN: 'dashboard_creator_sp_domain',
    DASHBOARD_CREATOR_SP_TIME_FILTER: 'dashboard_creator_sp_time_filter',
    DASHBOARD_CURATED_SP_DOMAIN: 'dashboard_curated_sp_domain',
    DASHBOARD_CURATED_SP_TIME_FILTER: 'dashboard_curated_sp_time_filter',
    DASHBOARD_LEADERBOARD_DOMAIN: 'dashboard_leaderboard_domain',
    DASHBOARD_LEADERBOARD_OUTCOME: 'dashboard_leaderboard_outcome',

    // Model details preferences
    PERFORMANCE_METRIC_INTERVAL: 'performance_metric_interval',
    PERFORMANCE_SCOPE: 'performance_scope',

    // Sidebar model lists (lab-scoped via {labHash}: prefix)
    SIDEBAR_RECENT_MODEL_IDS: 'sidebar_recent_model_ids',
    SIDEBAR_STARRED_MODEL_IDS: 'sidebar_starred_model_ids',
    SIDEBAR_RECENT_COLLAPSED: 'sidebar_recent_collapsed',
    SIDEBAR_STARRED_COLLAPSED: 'sidebar_starred_collapsed',
    SIDEBAR_INPROGRESS_COLLAPSED: 'sidebar_inprogress_collapsed',
    SIDEBAR_ALL_COLLAPSED: 'sidebar_all_collapsed',
    SIDEBAR_ARCHIVED_COLLAPSED: 'sidebar_archived_collapsed',
} as const;

interface ProfileState {
    profile: Profile | null;
    loading: boolean;
    error: Error | null;
    // Actions
    refreshProfile: (api?: ApiServices) => Promise<void>;
    setProfile: (profile: Profile | null) => void;
    updateProfile: (partialProfile: Partial<Profile>) => void;
    getSelectedLab: () => Lab | null;
    loadProfile: (api: ApiServices, options?: { isRefresh?: boolean }) => Promise<void>;
    setLoading: (loading: boolean) => void;
    setError: (error: Error | null) => void;
    setPreference: (api: ApiServices, name: string, value: string) => void;
    getPreference: (name: string) => string | undefined;
    reset: () => void;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
    profile: null,
    loading: true,
    error: null,

    setProfile: (profile) => set({ profile }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setPreference: (api: ApiServices, name: string, value: string) => {
        const profile = get().profile;
        if (!profile) return;

        // Initialize preferences if it doesn't exist
        const currentPreferences = profile.preferences || [];

        // Find index of the preference
        const idx = currentPreferences.findIndex(p => p.name === name);
        let updatedPreferences;

        // If value is empty string, REMOVE the preference entirely
        if (value === '') {
            if (idx !== -1) {
                // Remove the preference from the array
                updatedPreferences = [
                    ...currentPreferences.slice(0, idx),
                    ...currentPreferences.slice(idx + 1)
                ];
            } else {
                // Preference doesn't exist, nothing to remove
                updatedPreferences = currentPreferences;
            }
        } else if (idx !== -1) {
            // Update existing preference
            updatedPreferences = [
                ...currentPreferences.slice(0, idx),
                { ...currentPreferences[idx], value },
                ...currentPreferences.slice(idx + 1)
            ];
        } else {
            // Add new preference
            updatedPreferences = [...currentPreferences, { name, value }];
        }

        set({ profile: { ...profile, preferences: updatedPreferences } });
        // save it to the server
        if (value === '') {
            // Delete preference when value is empty
            api.profile.deletePreference({ name });
        } else {
            // Upsert preference for non-empty values
            api.profile.upsertPreference({ name, value });
        }
    },

    getPreference: (name: string) => {
        const profile = get().profile;
        return profile?.preferences?.find(p => p.name === name)?.value;
    },

    loadProfile: async (api, options?) => {
        const isRefresh = options?.isRefresh ?? false;
        try {
            //TODO: check loading state, we are setting this somewhere and not finally unsetting it
            // set({ loading: true });
            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');
            const user = useUserStore.getState().user;

            if (!accessToken || !refreshToken || !user) {
                if (!isRefresh) {
                    set({ profile: null });
                }
                return;
            }

            // Run all profile data fetches in parallel
            const [profileData, labs] = await Promise.all([
                api.profile.getOwnProfile(),
                api.lab.getProfileLabs(user.id)
            ]);

            if (!profileData) {
                return;
            }
            const updatedProfile = {
                ...profileData,
                labs,
                preferences: profileData.preferences || [], // Initialize if missing
            };
            // On background refresh, skip the store update if nothing meaningful changed.
            // This avoids a re-render cascade across all profile-subscribing components.
            if (isRefresh) {
                const current = get().profile;
                if (current
                    && current.name === updatedProfile.name
                    && current.handle === updatedProfile.handle
                    && current.email === updatedProfile.email
                    && current.picture === updatedProfile.picture
                    && current.bio === updatedProfile.bio
                    && current.flair === updatedProfile.flair
                    && current.selectedLabId === updatedProfile.selectedLabId
                    && current.subscription?.status === updatedProfile.subscription?.status
                    && current.subscription?.planId === updatedProfile.subscription?.planId
                    && current.labs?.length === updatedProfile.labs?.length
                    && current.labs?.every((lab, i) => lab.id === updatedProfile.labs?.[i]?.id)
                    && current.preferences?.length === updatedProfile.preferences?.length
                ) {
                    return;
                }
            }
            set({ profile: updatedProfile });
        } catch (error) {
            console.error('Failed to load profile:', error);
            if (!isRefresh) {
                set({ profile: null });
            }
        } finally {
            if (!isRefresh) {
                set({ loading: false });
            }
        }
    },

    refreshProfile: async (api?: ApiServices) => {
        const user = useUserStore.getState().user;
        if (user && api) {
            await get().loadProfile(api, { isRefresh: true });
        } else {
            console.error('no user or api passed to refreshProfile');
        }
    },

    updateProfile: (partialProfile: Partial<Profile>) => {
        set((state) => {
            if (!state.profile) return state;
            return {
                ...state,
                profile: {
                    ...state.profile,
                    ...partialProfile,
                }
            };
        });
    },

    getSelectedLab: () => {
        const profile = get().profile;
        if (!profile?.labs?.length) return null;

        if (profile?.labs?.length === 1) {
            return profile.labs[0];
        }

        // Check if user has a selected lab preference
        const selectedLabPref = profile.preferences?.find(p => p.name === PREFERENCE_KEYS.SELECTED_LAB_ID);
        if (selectedLabPref) {
            const selectedLab = profile.labs.find(lab => lab.id === selectedLabPref.value);
            if (selectedLab) return selectedLab;
            // If preference points to non-existent lab, fall through to default
        }

        // Default to first lab in the list
        return profile.labs[0];
    },

    reset: () => {
        set({ profile: null });
    }
})); 